import {
  ELEMENT_CONTROLLER_ID,
  ELEMENT_CONTROLLER_ROOT_ATTRIBUTE,
} from './base-controllers/ElementController';
import { GLOBAL_CONTROLLER_ID } from './base-controllers/GlobalController';
import { getData, isElement, isInDom, onReady, onLoad } from './utils/dom';

/**
 * Manually create a new instance of the specified element controller
 *
 * If for some reason initializer can not be used.
 *
 * @param {Function} ControllerClass - Element controller class.
 * @param {HTMLElement} el - Node to init on.
 * @returns {object} The controller instance.
 */
export function initElementController(ControllerClass, el) {
  const instance = new ControllerClass();

  // DOMContentLoaded
  instance._init(el);
  instance._didInit = true;

  // Window load
  instance._load();
  instance._didLoad = true;

  return instance;
}

const PRE_INIT_CLASS = 'no-js-init';
const PRE_LOAD_CLASS = 'no-js-load';
const INIT_DONE_CLASS = 'js-init';
const LOAD_DONE_CLASS = 'js-load';

const LAZY_STATUS_INIT = 'LAZY';
const LAZY_STATUS_LOADING = 'LOADING';
const LAZY_STATUSES = [LAZY_STATUS_INIT, LAZY_STATUS_LOADING];

/**
 * Exported for tests.
 */
export const DEFAULT_ROOT_EL = document;

/**
 * Class for handling controllers lifecycle.
 *
 * Usage:
 *
 *     // Import the initializer instance, an immediately loaded controller.
 *     // and an ID used to lazy load a controller.
 *     import {
 *       initializer,
 *       SomethingCommon,
 *       LIGHTBOX_CONTROLLER_ID,
 *     } from 'js-common';
 *
 *     // Another immediately loaded controller.
 *     import MyPageThing from './MyPageThing';
 *
 *     // IDs for lazy controllers must be kept in a separate file so that they
 *     // can be imported without touching the controller file. The controller
 *     // imports the ID in the same way and defines it on the class as
 *     // `static id = LAZY_THING_CONTROLLER_ID;`.
 *     import { LAZY_THING_CONTROLLER_ID } from './constants';
 *
 *     initializer.register([
 *       MyPageThing,
 *       SomethingCommon,
 *
 *       // LazyThing will only be loaded if there is an element in the DOM
 *       // that needs it.
 *       [LAZY_THING_CONTROLLER_ID, () => import(/* webpackChunkName: 'LazyThing' * / './LazyThing')],
 *
 *       // Lazy controllers from js-common can NOT be imported the usual way
 *       // via 'js-common', but must use their own paths.
 *       [LIGHTBOX_CONTROLLER_ID, () => import(/* webpackChunkName: 'Lightbox' * / 'js-common/Lightbox')],
 *     ]);
 *     initializer.init();
 *
 * Without webpackChunkName the chunk will just be named with a number, so it's
 * inclusion is recommended. All element controllers in js-common are available
 * as aliases under `js-common/<ControllerName>`.
 *
 * Available options:
 *
 * - setClasses {boolean} Set classes on the html element on DOM ready and
 *   window load events, both after all controller callbacks have run. Defaults
 *   to true.
 *
 * Internal terminology:
 *
 * - instance: a controller object instantiated from a controller class.
 * - root: primarily refers to a DOM node from which element controller nodes
 *   are located and instances created. A '#my-page-section' root can contain
 *   any number of controllers. It should NOT refer to an element controller's
 *   root element, use other variable names for those.
 */
export class Initializer {
  /**
   * Available controller classes.
   *
   * @member {object} _controllers - Keys are controller IDs, values are
   *   objects with `Controller` (controller class/constructor), `_baseId`
   *   (base controller ID) and `id` (controller ID). For lazy controllers
   *   the value is just a status string until it's loaded.
   */
  _controllers = {};

  /**
   * Loader functions for lazy controller classes.
   *
   * @member {object} _controllerLoaders - Keys are controller IDs, values are
   *   loader functions that should return a dynamic import call.
   */
  _controllerLoaders = {};

  /**
   * Temporary storage of root elements for lazy loaded controllers.
   *
   * @see _loadLazyController()
   */
  _lazyRoots = {};

  /**
   * Data for instantiated controllers.
   *
   * @see _createController()
   * @member {WeakMap} _instanceData - The keys are DOM nodes that has the
   *   controller attribute defined, values are arrays of objects that contain
   *   `instance` and `initArgs`.
   */
  _instanceData = new WeakMap();

  /**
   * Default options.
   *
   * @see setOptions()
   */
  _opt = {
    setClasses: true,
  };

  /**
   * Init status flag.
   *
   * @see _init()
   */
  _didDefaultInit = false;

  /**
   * Set options.
   *
   * @param {object} options - Default option overrides.
   */
  setOptions(options) {
    this._opt = {
      ...this._opt,
      ...options,
    };
  }

  /**
   * Register available controllers.
   *
   * @param {Array.<Function|Array>} controllerClasses - Controller classes or
   *   tuples of [controllerId, loaderFunction] where loaderFunction is a
   *   function that returns an import() call.
   * @returns {object} `this` for chaining.
   */
  register(controllerClasses) {
    controllerClasses.forEach((cls) => {
      if (
        Array.isArray(cls) &&
        typeof cls[0] === 'string' &&
        typeof cls[1] === 'function'
      ) {
        this._controllers[cls[0]] = LAZY_STATUS_INIT;
        this._controllerLoaders[cls[0]] = cls[1];
      } else if (typeof cls === 'function') {
        this._setControllerData(cls);
      } /* istanbul ignore next */ else if (
        process.env.NODE_ENV === 'development'
      ) {
        throw new TypeError(
          'Invalid controller, must be a controller class or a tuple with [controllerId, loaderFunction].',
        );
      }
    });

    return this;
  }

  /**
   * Create a controller class instance and store it.
   *
   * @param {string} id - Controller ID.
   * @param {Element} controllerEl - Controller root element (the one with
   *   the controller data attribute).
   * @param  {...*} initArgs - Arguments to pass to the controller's init().
   */
  _createController(id, controllerEl, ...initArgs) {
    if (!this._instanceData.get(controllerEl)) {
      this._instanceData.set(controllerEl, []);
    }

    const instanceData = this._instanceData.get(controllerEl);
    const { Controller } = this._controllers[id];

    // Skip if there's already an instance of the controller for the element.
    // Don't use hasCreatedInstance from below since that's for lazy loading
    // hints.
    if (
      instanceData.length &&
      instanceData.some(({ instance }) => instance.constructor === Controller)
    ) {
      // Controllers in js-common (like Lightbox) should use init when needed
      // to support being used without an observer. This will likely result
      // in duplicate init calls that shouldn't trigger a warning since there
      // is nothing for the app to do about it.
      /* istanbul ignore next */
      if (process.env.NODE_ENV === 'development' && !this._observer) {
        // eslint-disable-next-line no-console
        console.warn(
          `There is already an instance of '${id}' on element, skipping init`,
          controllerEl,
        );
      }
      return;
    }

    const instance = new Controller();
    this._controllers[id].hasCreatedInstance = true;
    instanceData.push({ instance, initArgs });
  }

  /**
   * Create an element controller class instance and store it.
   *
   * @param {string} id - Controller ID.
   * @param {Element} controllerEl - Controller root element (the one with
   *   the controller data attribute).
   */
  _createElementController(id, controllerEl) {
    /* istanbul ignore next */
    if (
      process.env.NODE_ENV === 'development' &&
      this._controllers[id]._baseId !== ELEMENT_CONTROLLER_ID
    ) {
      throw new Error(
        `Controllers bound to elements should extend ${ELEMENT_CONTROLLER_ID}`,
      );
    }
    this._createController(id, controllerEl, controllerEl);
  }

  /**
   * Load a lazy controller and initialize it when done.
   *
   * @param {string} id - Controller ID.
   * @param {Element} rootEl - Root element that the controller is created in.
   */
  async _loadLazyController(id, rootEl) {
    if (!LAZY_STATUSES.includes(this._controllers[id])) {
      throw new Error('Invalid controller state for lazy loading');
    }

    // Handle the same controller being initialized for multiple root elements
    // while loading.
    if (this._controllers[id] === LAZY_STATUS_LOADING) {
      this._lazyRoots[id].add(rootEl);
      return;
    }
    this._lazyRoots[id] = new Set();
    this._lazyRoots[id].add(rootEl);

    this._controllers[id] = LAZY_STATUS_LOADING;

    const loader = this._controllerLoaders[id];
    const { default: controllerClass } = await loader();

    // Global controllers always run right away and the main bundle should
    // already be async, no point in splitting there.
    /* istanbul ignore next */
    if (
      process.env.NODE_ENV === 'development' &&
      controllerClass._baseId !== ELEMENT_CONTROLLER_ID
    ) {
      throw new Error('Only element controllers should be lazy loaded');
    }

    // Will overwrite the string status flag.
    this._setControllerData(controllerClass);

    // Loop over each stored root element since the same controller can be
    // initialized multiple times while loading.
    this._lazyRoots[id].forEach((lazyRootEl) => {
      // A stored node can be removed while the controller is loading.
      if (lazyRootEl !== DEFAULT_ROOT_EL && !isInDom(lazyRootEl)) {
        return;
      }

      const elementControllerRoots = this._getElementControllerRoots(
        lazyRootEl,
        id,
      );
      elementControllerRoots.forEach((controllerEl) => {
        this._createElementController(id, controllerEl);
      });

      onReady(this._onReady.bind(this, lazyRootEl, ...elementControllerRoots));
      onLoad(this._onLoad.bind(this, lazyRootEl, ...elementControllerRoots));
    });

    delete this._lazyRoots[id];
  }

  /**
   * Initialize controllers for elements contained inside the specified element.
   *
   * @see _init()
   * @param {Element} [rootEl] - The element to initialize controllers in.
   *   Defaults to document, which means all possible controllers on the
   *   whole page are initialized.
   * @example
   *
   * // Initialize all controllers.
   * initializer.init();
   *
   * // Only initialize controllers inside the supplied lightbox container.
   * initializer.init(myLightboxElement);
   */
  init(rootEl = DEFAULT_ROOT_EL) {
    // Ensure the DOM is ready before getting elements.
    onReady(this._init.bind(this, rootEl));
  }

  /**
   * Run the init on DOM ready.
   *
   * @param {Element} rootEl - The element to initialize controllers in.
   */
  _init(rootEl) {
    // Init all global controllers when running in the default/global context.
    // Only allow a single global init to prevent multiple identical instances
    // of global controllers.
    if (rootEl === DEFAULT_ROOT_EL) {
      if (this._didDefaultInit) {
        throw new Error('Only run the default init once.');
      }
      this._didDefaultInit = true;
      Object.values(this._controllers)
        .filter((c) => c._baseId === GLOBAL_CONTROLLER_ID)
        .forEach(({ id }) => {
          this._createController(id, DEFAULT_ROOT_EL);
        });

      if (this._opt.setClasses) {
        document.documentElement.classList.remove(PRE_INIT_CLASS);
        document.documentElement.classList.add(INIT_DONE_CLASS);
      }

      window.addEventListener('popstate', this._onBeforeNavigation);
      window.addEventListener('beforeunload', this._onBeforeNavigation);
    }

    // Init all element controllers inside the specified root element.
    const elementControllerRoots = this._getElementControllerRoots(rootEl);
    elementControllerRoots.forEach((controllerEl) => {
      // An element may have multiple controllers.
      const ids = getData(
        controllerEl,
        ELEMENT_CONTROLLER_ROOT_ATTRIBUTE.replace('data-', ''),
      ).split(' ');
      ids.forEach((id) => {
        const controllerData = this._controllers[id];
        // Lazy controller status is set with strings.
        if (typeof controllerData === 'string') {
          this._loadLazyController(id, rootEl);
        } else if (typeof controllerData === 'object') {
          this._createElementController(id, controllerEl);
        } else {
          /* istanbul ignore next */
          if (process.env.NODE_ENV === 'development') {
            if (controllerData === undefined) {
              // eslint-disable-next-line no-console
              console.warn(
                `No controller data defined for '${id}', has it been registered?`,
                controllerEl,
              );
            } else {
              // eslint-disable-next-line no-console
              console.warn(
                `Expected string or object for '${id}' controller data, got`,
                controllerData,
                'on element',
                controllerEl,
              );
            }
          }
          // Don't throw right away since that may prevent other controllers
          // from initiating.
          setTimeout(() => {
            throw new Error(`No valid controller registered for ID '${id}'`);
          }, 50);
        }
      });
    });

    // Some DX checks. Only run for the default init to prevent the same
    // messages appearing multiple times.
    /* istanbul ignore next */
    if (process.env.NODE_ENV === 'development' && rootEl === DEFAULT_ROOT_EL) {
      setTimeout(() => {
        // Inform about lazy loading candidates.
        Object.values(this._controllers)
          .filter((c) => c.hasCreatedInstance === false)
          .forEach(({ Controller }) => {
            // eslint-disable-next-line no-console
            console.warn(
              `Controller '${Controller.id}' doesn't have an instance on this page. Consider lazy loading if it's large enough to be worth it.`,
            );
          });
      }, 100);
    }

    onReady(this._onReady.bind(this, rootEl, ...elementControllerRoots));
    onLoad(this._onLoad.bind(this, rootEl, ...elementControllerRoots));
  }

  /**
   * Initialize and destroy controllers on added or removed elements.
   *
   * @param {Array.<MutationRecord>} mutations - Mutation records.
   */
  _onMutation = (mutations) => {
    mutations.forEach((record) => {
      // Destroy controllers on removed Element nodes.
      record.removedNodes.forEach((node) => {
        if (isElement(node) && !node.isConnected) {
          this.destroy(node);
        }
      });

      // Init controllers on added Element nodes.
      record.addedNodes.forEach((node) => {
        if (isElement(node) && node.isConnected) {
          this.init(node);
        }
      });
    });
  };

  /**
   * Initialize controllers globally and automatically handle `init()` and
   * `destroy()` when elements are added and removed.
   *
   * Removes the need to call `init()` manually in controllers that insert or
   * remove DOM nodes.
   */
  autoInit() {
    this._observer = new MutationObserver(this._onMutation);
    this._observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    this.init();
  }

  /**
   * Destroy controllers for elements contained inside the specified element.
   *
   * @param {Element} [rootEl] - The element to destroy controllers in. Defaults
   *   to document, which means all possible controllers on the whole page are
   *   destroyed.
   * @returns {boolean} True if all controllers were destroyed successfully.
   * @example
   *
   * // Destroy all controllers.
   * initializer.destroy();
   *
   * // Only destroy controllers inside the supplied lightbox container.
   * initializer.destroy(myLightboxElement);
   */
  destroy(rootEl = DEFAULT_ROOT_EL) {
    const elementControllerRoots = this._getElementControllerRoots(rootEl);

    // If any element has any instance that returns false from shouldDestroy.
    const shouldSkipDestroy = elementControllerRoots.some((el) => {
      const instances = this._instanceData.get(el);
      if (!instances) {
        return false;
      }

      return instances.some(({ instance }) => {
        const shouldDestroy = instance.shouldDestroy();
        // Not unlikely to get undefined from a missing return, which would
        // result in the same as false and is probably not what's intended.
        const shouldDestroyType = typeof shouldDestroy;
        if (shouldDestroyType !== 'boolean') {
          throw new TypeError(
            `Expected a boolean from ${instance.constructor.id}.shouldDestroy(), got ${shouldDestroyType}`,
          );
        }
        return !shouldDestroy;
      });
    });

    if (shouldSkipDestroy) {
      return false;
    }

    elementControllerRoots.forEach((el) => {
      const instances = this._instanceData.get(el);
      if (!instances) {
        return;
      }

      // Destroy each instance.
      instances.forEach(({ instance }) => {
        instance.destroy();
      });

      // Remove all instance data for the node.
      this._instanceData.delete(el);
    });

    return true;
  }

  /**
   * Get element controller nodes (elements with the controller data attribute)
   * contained in the specified element, including the element itself.
   *
   * @param {Element} el - Element to get controllers in.
   * @param {string} [id] - Only get controllers with a specific ID.
   * @returns {Array.<Element>} DOM nodes.
   */
  _getElementControllerRoots(el, id = '') {
    const selector = id
      ? `[${ELEMENT_CONTROLLER_ROOT_ATTRIBUTE}~="${id}"]`
      : `[${ELEMENT_CONTROLLER_ROOT_ATTRIBUTE}]`;
    const elements = Array.from(el.querySelectorAll(selector));

    return el.matches && el.matches(selector) ? elements.concat(el) : elements;
  }

  /**
   * Set a controller's data for later use.
   *
   * @throws {Error} - If the ID configuration is invalid.
   * @param {Function} controllerClass - Controller class.
   */
  _setControllerData(controllerClass) {
    const { _baseId, id, dependencies } = controllerClass;
    // IMPORTANT! Don't use `name` for any important logic since it will be
    // mangled to a single letter when minified.
    let { name } = controllerClass;

    // Validate controller class name on dev.
    /* istanbul ignore next */
    if (process.env.NODE_ENV === 'development') {
      if (!name) {
        throw new Error(
          `Controller class (baseId: '${_baseId}', id: '${id}') is missing a name`,
        );
      }
      // When tree shaking dev code via terser, the class name is prefixed
      // with the file name for some reason (e.g. class Foo {} in Foo.js
      // results in class Foo_Foo). Try to handle it.
      const nameParts = name.split('_');
      if (nameParts.length === 2 && nameParts[1].includes(nameParts[0])) {
        name = nameParts[1];
      }
    }

    if (!_baseId) {
      throw new Error(
        `Controller '${name}' is missing a static '_baseId' property, which should be set on its parent`,
      );
    }
    if (!id) {
      throw new Error(`Controller '${name}' is missing a static 'id' property`);
    }

    // Dev only checks.
    /* istanbul ignore next */
    if (process.env.NODE_ENV === 'development') {
      // Validate controller ID.
      if (id !== name) {
        throw new Error(
          `A controller's \`id\` should match the class name, got id '${id}' for class '${name}'`,
        );
      }
      if (name.includes('Controller')) {
        throw new Error(
          `Including 'Controller' in a controller name is redundant (class '${name}')`,
        );
      }

      // Inform about missing dependencies.
      setTimeout(() => {
        const controllerIds = Object.keys(this._controllers);
        (dependencies || []).forEach((dep) => {
          if (!controllerIds.includes(dep)) {
            // eslint-disable-next-line no-console
            console.warn(
              `Controller '${id}' depends on controller '${dep}' which isn't registered, '${id}' may not work properly.`,
            );
          }
        });
      }, 200);
    }

    this._controllers[id] = {
      Controller: controllerClass,
      hasCreatedInstance: false,
      _baseId,
      id,
    };
  }

  /**
   * Get the controller instance data objects for all specified elements.
   *
   * @param {...Element} elements - Elements with instances.
   * @returns {Array.<object>} Instance data objects with `instance` and
   *   `initArgs`.
   */
  _getInstanceData(...elements) {
    return (
      // Ensure unique elements
      [...new Set(elements)]
        // Handle things like a null result from querySelector.
        .map((el) => (el ? this._instanceData.get(el) : null))
        // Instances may be undefined, e.g. for currently lazy loading elements,
        // or null from above.
        .filter(Boolean)
        // The map will result in an array of instance arrays, flatten it.
        .flat()
    );
  }

  /**
   * Get the controller instance data objects for all global controllers.
   *
   * @returns {Array.<object>} Instance data objects with `instance` and
   *   `initArgs`.
   */
  _getGlobalInstanceData() {
    return this._getInstanceData(DEFAULT_ROOT_EL);
  }

  /**
   * On DOM ready. Initialize all instances for the specified elements.
   *
   * @param {...Element} elements - The elements with controller instances that
   *   should be initialized.
   */
  _onReady(...elements) {
    this._getInstanceData(...elements).forEach(({ instance, initArgs }) => {
      if (!instance._didInit) {
        instance._init(...initArgs);
        instance._didInit = true;
      }
    });

    if (this._opt.setClasses && elements.includes(DEFAULT_ROOT_EL)) {
      document.documentElement.classList.remove(PRE_INIT_CLASS);
      document.documentElement.classList.add(INIT_DONE_CLASS);
    }
  }

  /**
   * On window load. Call load handler on all instances for the specified
   * elements.
   *
   * @param {...Element} elements - The elements with controller instances that
   *   should have the load handlers called.
   */
  _onLoad(...elements) {
    // Assumes _init always runs before or at the same time, never after.
    // According to the MDN docs on readyState, a state of 'complete' indicates
    // that the load event is ABOUT to fire, so this should always come after.
    // Browsers and bugs though...
    // A small timeout should hopefully add enough safety and possibly give
    // browsers some extra time to paint everything that loaded as well.
    // https://developer.mozilla.org/en-US/docs/Web/API/Document/readyState
    // TODO: think about this.
    setTimeout(() => {
      this._getInstanceData(...elements).forEach(({ instance }) => {
        if (!instance._didLoad) {
          instance._load();
          instance._didLoad = true;
        }
      });

      if (this._opt.setClasses && elements.includes(DEFAULT_ROOT_EL)) {
        document.documentElement.classList.remove(PRE_LOAD_CLASS);
        document.documentElement.classList.add(LOAD_DONE_CLASS);
      }
    }, 10);
  }

  /**
   * On `beforeunload` or `popstate`. Call `onBeforeNavigation` handler on all
   * controller instances.
   *
   * @param {Event} event
   */
  _onBeforeNavigation = (event) => {
    this._getElementControllerRoots(DEFAULT_ROOT_EL).forEach((el) => {
      const instances = this._instanceData.get(el);
      if (!instances) {
        return;
      }

      instances.forEach(({ instance }) => {
        instance.onBeforeNavigation(event);
      });
    });
  };
}

const initializer = new Initializer();

export default initializer;
